import { useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import Seo, { SITE } from '../../components/Seo/Seo';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChevronDown } from '@fortawesome/pro-regular-svg-icons';
import {
	TUITION_SEASON_TOTAL_LABEL,
	TUITION_MONTHLY_LABEL,
	TUITION_INSTALLMENTS,
	LATE_FEE_AMOUNT_LABEL,
	LATE_FEE_GRACE_DAYS,
	LATE_FEE_PAUSE_AFTER_DAYS,
	LATE_FEE_RELEASE_AFTER_DAYS,
	LATE_FEE_EFFECTIVE_LABEL,
} from '../../data/seasonData';

import './Policies.css';

// Policy content, grouped into collapsible sections. Each item's `id` is a
// deep-link target (e.g. /policies#makeups) — the FAQ page links to these so a
// fact lives in exactly one place. Answers may be a string or JSX.

// A — Summer 2026 (current season; shown first while summer is active).
const summerPolicies = [
	{
		id: 'summer-registration',
		q: 'Registration & payment',
		a: 'Summer programming is optional and billed separately from school-year tuition. Workshops are registered and paid online at coloryourvoice.com — spots are limited and confirmed once payment is complete. Summer private lessons are scheduled through the Parent/Student Portal.',
	},
	{
		id: 'summer-refunds',
		q: 'Workshop refunds',
		// Sourced from seed-workshops.js REFUND_POLICY so the displayed rule
		// matches what the checkout actually enforces (incl. the 48-hour clause).
		a: "You'll receive a full refund if you cancel at least 14 days before the workshop starts — or within 48 hours of registering, whichever is later, so a last-minute registration still has a window. After that, fees are non-refundable and non-creditable. If Color Your Voice ever cancels a workshop for low enrollment, you'll receive a full refund.",
	},
	{
		id: 'summer-vs-school-year',
		q: 'Summer & the school year',
		a: "June's payment is the final installment of school-year tuition, and regular school-year lessons do not run in June. Summer offerings are separate and optional.",
	},
];

// B — School-year program (returns this fall; here for reference).
const schoolYearPolicies = [
	{
		id: 'tuition',
		q: 'Tuition & billing',
		a: (
			<>
				<p>
					<strong>Flat Rate:</strong> {TUITION_SEASON_TOTAL_LABEL} for the
					school year, tax included.
				</p>
				<p>
					<strong>Payment Schedule:</strong> Tuition is spread across{' '}
					{TUITION_INSTALLMENTS} months, with {TUITION_INSTALLMENTS} payments
					of {TUITION_MONTHLY_LABEL} each. Payments are scheduled for
					the 1st of the month, beginning September 1st and ending with the
					last payment on June 1st.
				</p>
				<p>
					<strong>Autopay:</strong> All families must be on autopay.
				</p>
				<p>
					<strong>Payment Options:</strong> There are no processing fees —
					however you pay.
				</p>
				<ul>
					<li>
						Families who prefer to pay for the full year at once may do so
						by check.
					</li>
					<li>Personal checks are not accepted for monthly payments.</li>
					<li>
						Checks must be handed to either Anthony or Grace in person and
						should not be left at the studio.
					</li>
				</ul>
				<p>
					We do not charge by the lesson during the school year; it is a
					flat rate. All payments are processed through our{' '}
					<a href='/login' target='_blank' rel='noreferrer'>
						Parent/Student Portal
					</a>
					.
				</p>
			</>
		),
	},
	{
		id: 'late-payments',
		q: 'Late payments',
		a: (
			<>
				<p>
					Tuition is due on the 1st of each month. If a payment hasn't gone
					through by the {LATE_FEE_GRACE_DAYS}th, a{' '}
					<strong>{LATE_FEE_AMOUNT_LABEL} late fee</strong> is added to the
					account. This begins {LATE_FEE_EFFECTIVE_LABEL}.
				</p>
				<p>
					The fee doesn't apply when a current payment method is on file and
					the charge didn't go through for a reason outside your control —
					we'll get in touch and sort it out with you. It's added once per
					month at most, and we stop adding it after two months.
				</p>
				<p>
					If an account reaches{' '}
					<strong>{LATE_FEE_PAUSE_AFTER_DAYS} days past due</strong>, we pause
					lessons until the balance is current and hold your child's weekly
					time for another{' '}
					{LATE_FEE_RELEASE_AFTER_DAYS - LATE_FEE_PAUSE_AFTER_DAYS} days while
					we work it out together.
				</p>
				<p>
					If money is ever tight, tell us before the 1st rather than after. We
					can almost always find an arrangement, and we'd far rather do that
					than send anyone a fee.
				</p>
			</>
		),
	},
	{
		id: 'makeups',
		q: 'Make-up & cancellation',
		a: (
			<>
				<p className='pol-alert'>24-Hour Cancellation Policy</p>
				<ul>
					<li>
						All lessons require a 24-hour cancellation notice.{' '}
						<strong>
							Lessons canceled with less than 24 hours' notice are not
							eligible for a makeup.
						</strong>
					</li>
					<li>
						If you cancel at least 24 hours in advance, you will receive a
						makeup credit, good through the end of the school-year season
						(the exact date is published in the Portal each season) and
						subject to availability.
					</li>
					<li>
						<strong>
							Makeup lessons are subject to availability and are not
							guaranteed.
						</strong>
					</li>
				</ul>
				<p className='pol-alert'>Closure Due to Snow or Inclement Weather</p>
				<ul>
					<li>
						If the studio is closed due to snow or inclement weather, the
						24-hour cancellation policy is waived. You may opt for a makeup
						credit or attend your lesson virtually.
					</li>
					<li>
						If Color Your Voice cancels your lesson for any other reason, a
						makeup lesson will be guaranteed.
					</li>
				</ul>
			</>
		),
	},
	{
		id: 'attendance',
		q: 'Attendance, performances & what to bring',
		a: (
			<>
				<p>
					Weekly school-year lessons start and end depending on the day of
					the week you've selected.
				</p>
				<p>
					We value performance as an essential part of artistic growth and
					encourage opportunities both on and off our stage. Missing a
					lesson or two — for your school musical's tech week, for example —
					is fine; we'll try to make them up, though makeups aren't
					guaranteed. Please let us know about your child's performances so
					we can cheer them on!
				</p>
				<p>
					All scheduling runs through the Parent/Student Portal. To
					accommodate as many make-ups as possible, please cancel any
					lessons you already know you'll miss at the very start of the
					year — the earlier conflicts are marked, the more flexibility we
					have to support everyone's schedules.
				</p>
				<p>
					<strong>What to bring to every lesson:</strong>
				</p>
				<ul>
					<li>
						Sheet music (even if off-book — this is where we record notes
						throughout the year)
					</li>
					<li>Water</li>
				</ul>
			</>
		),
	},
	{
		id: 'rehearsals',
		q: 'Required rehearsals & recitals',
		a: (
			<>
				<p>
					In addition to weekly 45-minute private lessons, each season
					includes two recitals with required rehearsals. Specific dates are
					published in the Portal each season.
				</p>
				<p className='pol-alert'>
					Recital participation depends on your availability for these
					rehearsals.
				</p>
				<p>
					We also hold on-call group workshops/rehearsals during the season.
					Once recital plans are finalized, we may call your child to attend
					one or more of these sessions. Families receive at least one
					month's notice, and we do our best to accommodate schedules. These
					sessions give students the opportunity to work in small groups and
					ensembles and share developing performance skills.
				</p>
			</>
		),
	},
	{
		id: 'withdrawal',
		q: 'Withdrawal',
		a: "If a family decides to withdraw from the program before the school year is up, we require 30 days' notice. This notice period allows us to make necessary adjustments to schedules and plan accordingly.",
	},
	{
		id: 'closures',
		q: 'Scheduled closures',
		a: (
			<>
				<ul>
					<li>
						All scheduled closures (holidays, spring break, performance
						days, etc.) are listed on the calendar in our{' '}
						<a href='/login' target='_blank' rel='noreferrer'>
							Parent/Student Portal
						</a>
						.
					</li>
					<li>
						No makeups are provided for scheduled closures, so please
						consider this when choosing your lesson time for the year.
					</li>
				</ul>
			</>
		),
	},
];

// C — Always (evergreen, year-round).
const evergreenPolicies = [
	{
		id: 'health',
		q: 'Health & safety',
		a: 'The health and safety of our students are our top priority. Please do not attend lessons if you are feeling unwell. We offer virtual lessons as an alternative to in-person sessions for those who are unable to attend due to illness.',
	},
	{
		id: 'media',
		q: 'Media & privacy',
		a: (
			<>
				<p>
					We occasionally take photos or videos during lessons and
					performances to share on our website and social media. If you prefer
					that your child not be included in these media, please notify us in
					writing.
				</p>
				<p>
					This covers photos and video. For how we handle your information
					online — the details you send us, payments, and website cookies — see
					our <Link to='/privacy'>Privacy Policy</Link>.
				</p>
			</>
		),
	},
	{
		id: 'communication',
		q: 'Communication',
		a: (
			<>
				We primarily communicate via email and through the{' '}
				<a href='/login' target='_blank' rel='noreferrer'>
					Parent/Student Portal
				</a>{' '}
				for updates and important information. Please ensure you have access
				to the portal and that your contact information is accurate and up to
				date. Important studio updates, billing, scheduling, and materials
				will be posted or sent through these channels.
			</>
		),
	},
	{
		id: 'support',
		q: 'Supporting your artist at home',
		a: (
			<>
				<p>
					<strong>Practice space:</strong> Create a dedicated space and time
					for practice. Younger students may thrive with some parental
					encouragement, while older students often prefer privacy.
				</p>
				<p>
					<strong>Criticism &amp; feedback:</strong> After performances,
					avoid offering critical feedback. Instead, focus on encouragement:
					praise effort, highlight emotional impact, and ask open-ended
					questions to promote self-reflection.
				</p>
			</>
		),
	},
	{
		id: 'practice',
		q: 'Practice guidelines',
		a: (
			<>
				<p>Like training for a sport, consistency is key:</p>
				<ul>
					<li>
						<strong>Ages 8–12:</strong> 30–60 minutes total, 4–5 days/week
					</li>
					<li>
						<strong>Ages 13+:</strong> 60+ minutes total, 5–6 days/week
					</li>
				</ul>
				<p>
					Encourage breaks during longer sessions and rest if there's
					discomfort. The Parent/Student Portal can be used to log practice
					hours (recommended, but optional).
				</p>
			</>
		),
	},
	{
		id: 'auditions',
		q: 'Coping with audition outcomes',
		a: (
			<>
				<p>
					From our own years of performing, we know auditions can be an
					emotional rollercoaster. The healthiest approach is to focus on
					what your child can control — their preparation and effort. After
					auditions, avoid turning the moment into a lesson. Instead:
				</p>
				<ul>
					<li>Make their favorite snack</li>
					<li>Take them somewhere they love</li>
					<li>Listen without comparing or commenting</li>
					<li>
						Validate any mixed emotions they might have (they can be happy
						for their friend and feel upset — both feelings can exist)
					</li>
				</ul>
				<p>This support builds resilience and joy in their artistic journey.</p>
			</>
		),
	},
];

const GROUPS = [
	{ heading: 'Summer 2026 Programs', items: summerPolicies },
	{ heading: 'School-Year Program — returns this fall', items: schoolYearPolicies },
	{ heading: 'Always', items: evergreenPolicies },
];

const Policies = () => {
	const { hash } = useLocation();
	const openId = hash ? hash.replace('#', '') : undefined;

	// Deep-link: when arriving at /policies#some-id, scroll that section into
	// view (it's rendered open via the `open` attr below). The small delay lets
	// the page render first; scroll-margin-top (.pol details) keeps it below the
	// sticky nav.
	useEffect(() => {
		if (!openId) return undefined;
		const t = setTimeout(() => {
			document.getElementById(openId)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
		}, 250);
		return () => clearTimeout(t);
	}, [openId]);

	// First summer item is open by default; a hash overrides to open its target.
	const isOpen = (id) => (openId ? openId === id : id === 'summer-registration');

	return (
		<main className='cyv policies-page'>
			<Seo
				title='Policies | Color Your Voice'
				description='Color Your Voice studio policies — summer registration & refunds, school-year tuition, make-ups, and more.'
				canonical={`${SITE}/policies`}
			/>

			<div className='pol-wrap pol'>
				<div className='pol-card'>
					<div className='pol-card__head'>
						SESSION INFORMATION&nbsp; | &nbsp;STUDIO POLICIES&nbsp; | &nbsp;TERMS
						&amp; CONDITIONS
					</div>
					<div className='pol-card__body'>
						<p className='pol-intro'>
							We're in our summer season now — optional workshops and summer
							lessons, open to everyone — and our 2026–2027 school-year season
							begins the week of August 31, 2026. The school-year policies
							below apply to that season. Here's everything your family needs
							for the months ahead.
						</p>

						<div className='pol-sec'>
							{GROUPS.map((group) => (
								<section key={group.heading}>
									<h2 className='pol-sec__h'>{group.heading}</h2>
									{group.items.map((item) => (
										<details key={item.id} id={item.id} open={isOpen(item.id)}>
											<summary>
												{item.q} <FontAwesomeIcon icon={faChevronDown} />
											</summary>
											<div className='pol__a'>{item.a}</div>
										</details>
									))}
								</section>
							))}

							<div className='pol-foot'>
								<p>
									If you have any questions please feel free to{' '}
									<a href='mailto:sing@coloryourvoice.com'>contact us</a>.
								</p>
								<Link className='btn-cyv-teal' to='/contact'>
									Contact us
								</Link>
							</div>
						</div>
					</div>
				</div>
			</div>
		</main>
	);
};

export default Policies;
